<script setup lang="ts">
import { computed } from 'vue'
import { TModal, TButton, TInput } from '@theme/atoms'
import { useConfirmHost } from '@/composables/useConfirm'

const { state, accept, cancel } = useConfirmHost()

// When requireText is set, the confirm button stays disabled until the user
// types the exact phrase — a deliberate speed bump for irreversible actions.
const confirmDisabled = computed(
  () => state.requireText !== null && state.typed.trim() !== state.requireText,
)

function onModelUpdate(open: boolean) {
  // TModal emits false on backdrop/escape/close — treat as cancel (fail-closed).
  if (!open) cancel()
}

function onEnter() {
  if (!confirmDisabled.value) accept()
}
</script>

<template>
  <TModal
    :modelValue="state.open"
    :title="state.title"
    size="sm"
    @update:modelValue="onModelUpdate"
  >
    <div class="confirm__body">
      <p v-if="state.message" class="confirm__message">{{ state.message }}</p>
      <div v-if="state.requireText !== null" class="confirm__require">
        <label class="confirm__require-label">
          請輸入 <code>{{ state.requireText }}</code> 以確認
        </label>
        <TInput
          v-model="state.typed"
          :placeholder="state.requireText"
          @keyup.enter="onEnter"
        />
      </div>
    </div>

    <template #footer>
      <TButton variant="secondary" @click="cancel">{{ state.cancelText }}</TButton>
      <TButton
        :variant="state.variant === 'danger' ? 'danger' : 'primary'"
        :disabled="confirmDisabled"
        @click="accept"
      >
        {{ state.confirmText }}
      </TButton>
    </template>
  </TModal>
</template>

<style scoped>
.confirm__body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.confirm__message {
  font-size: var(--typography-body-default-size, 14px);
  color: var(--colors-text-primary);
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
}

.confirm__require {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.confirm__require-label {
  font-size: var(--typography-body-small-size, 13px);
  color: var(--colors-text-secondary);
}

.confirm__require-label code {
  padding: 1px 6px;
  border-radius: var(--radius-sm, 4px);
  background: var(--colors-background-surface);
  color: var(--colors-error);
  font-weight: 600;
}
</style>
